import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useToast } from '../../contexts/ToastContext.jsx';
import LoadingSpinner from '../LoadingSpinner.jsx';
import { apiRequest } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { ApiError } from '../../utils/apiClient.js';

/**
 * Admin component for managing phone number assignments with advanced tools
 */
const PhoneAssignmentManager = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const { showToast } = useToast();

  // State management
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, assigned, unassigned, inconsistent
  const [sortBy, setSortBy] = useState('phone_number');
  const [sortOrder, setSortOrder] = useState('asc');

  // Bulk operations state
  const [selectedAssignments, setSelectedAssignments] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkOperation, setBulkOperation] = useState('');

  // Assignment state detection
  const [inconsistentStates, setInconsistentStates] = useState([]);
  const [showInconsistentOnly, setShowInconsistentOnly] = useState(false);

  // Modal states
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);

  // Available users and parrots for assignment
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableParrots, setAvailableParrots] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingParrots, setLoadingParrots] = useState(false);

  /**
   * Fetch all phone number assignments with detailed information
   */
  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getToken();
      const response = await apiRequest('/api/admin/phone-numbers/all', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      const phoneNumbers = data.phoneNumbers || [];

      // Detect inconsistent states
      const inconsistent = phoneNumbers.filter(phone => {
        // Check for various inconsistency patterns
        // Note: Having a user but no parrot is NOT inconsistent - it's a valid state
        const hasParrotButNoUser = phone.assigned_parrot_name && !phone.assigned_user_id;
        const statusMismatch = (phone.assigned_user_id && phone.status === 'unassigned') ||
                              (!phone.assigned_user_id && phone.status === 'assigned');
        
        return hasParrotButNoUser || statusMismatch;
      });

      setAssignments(phoneNumbers);
      setInconsistentStates(inconsistent);

    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError(error.message || 'Failed to load phone number assignments');
      showToast('Failed to load phone number assignments', 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, showToast]);

  /**
   * Fetch available users for assignment
   */
  const fetchAvailableUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const token = await getToken();
      const response = await apiRequest('/api/admin/users?limit=1000&status=active', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setAvailableUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast('Failed to load users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  }, [getToken, showToast]);

  /**
   * Fetch available parrots for assignment
   */
  const fetchAvailableParrots = useCallback(async () => {
    try {
      setLoadingParrots(true);
      const token = await getToken();
      const response = await apiRequest('/api/admin/parrots?limit=1000&status=active', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setAvailableParrots(data.parrots || []);
    } catch (error) {
      console.error('Error fetching parrots:', error);
      showToast('Failed to load parrots', 'error');
    } finally {
      setLoadingParrots(false);
    }
  }, [getToken, showToast]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      await fetchAssignments();
      showToast('Assignments refreshed successfully', 'success');
    } catch {
      // Error already handled in fetchAssignments
    } finally {
      setRefreshing(false);
    }
  }, [fetchAssignments, showToast]);

  /**
   * Fix inconsistent assignment state
   */
  const handleFixInconsistentState = useCallback(async (assignment) => {
    try {
      const token = await getToken();
      
      // Determine the fix action based on the inconsistency
      let fixAction = 'unassign'; // Default action
      let requestBody = {};

      if (assignment.assigned_user_id && !assignment.assigned_parrot_name) {
        // Has user but no parrot - unassign from user
        fixAction = 'unassign';
      } else if (assignment.assigned_parrot_name && !assignment.assigned_user_id) {
        // Has parrot but no user - this shouldn't happen, unassign completely
        fixAction = 'unassign';
      }

      const response = await apiRequest(`/api/admin/phone-numbers/${assignment.id}/${fixAction}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      // Refresh assignments to reflect changes
      await fetchAssignments();
      showToast('Inconsistent state fixed successfully', 'success');

    } catch (error) {
      console.error('Error fixing inconsistent state:', error);
      showToast(error.message || 'Failed to fix inconsistent state', 'error');
    }
  }, [getToken, fetchAssignments, showToast]);

  /**
   * Handle bulk assignment operation
   */
  const handleBulkOperation = useCallback(async (operation, targetUserId = null) => {
    if (selectedAssignments.size === 0) {
      showToast('Please select assignments to perform bulk operations', 'error');
      return;
    }

    try {
      const token = await getToken();
      const assignmentIds = Array.from(selectedAssignments);

      // Perform bulk operation based on type
      const promises = assignmentIds.map(async (assignmentId) => {
        const assignment = assignments.find(a => a.id === assignmentId);
        if (!assignment) return;

        let endpoint = '';
        let method = 'POST';
        let body = {};

        switch (operation) {
          case 'unassign':
            endpoint = `/api/admin/phone-numbers/${assignmentId}/unassign`;
            break;
          case 'assign':
            if (!targetUserId) throw new Error('Target user required for assignment');
            endpoint = `/api/admin/phone-numbers/${assignmentId}/assign`;
            body = { userId: targetUserId };
            break;
          case 'release':
            endpoint = `/api/admin/phone-numbers/${assignmentId}/release`;
            method = 'DELETE';
            break;
          default:
            throw new Error('Unknown bulk operation');
        }

        const response = await apiRequest(endpoint, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
        });

        if (!response.ok) {
          const errorMessage = await parseApiError(response);
          throw new ApiError(`${assignment.phone_number}: ${errorMessage}`, response.status, response);
        }

        return response.json();
      });

      await Promise.all(promises);

      // Clear selection and refresh
      setSelectedAssignments(new Set());
      setShowBulkActions(false);
      await fetchAssignments();

      showToast(`Bulk ${operation} completed successfully for ${assignmentIds.length} assignments`, 'success');

    } catch (error) {
      console.error('Bulk operation error:', error);
      showToast(error.message || `Failed to perform bulk ${operation}`, 'error');
    }
  }, [selectedAssignments, assignments, getToken, fetchAssignments, showToast]);

  /**
   * Handle assignment selection
   */
  const handleAssignmentSelection = (assignmentId, selected) => {
    const newSelection = new Set(selectedAssignments);
    if (selected) {
      newSelection.add(assignmentId);
    } else {
      newSelection.delete(assignmentId);
    }
    setSelectedAssignments(newSelection);
  };

  /**
   * Handle select all/none
   */
  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      const allIds = filteredAndSortedAssignments.map(a => a.id);
      setSelectedAssignments(new Set(allIds));
    } else {
      setSelectedAssignments(new Set());
    }
  };

  /**
   * Filter and sort assignments
   */
  const filteredAndSortedAssignments = React.useMemo(() => {
    let filtered = assignments;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(assignment =>
        assignment.phone_number?.toLowerCase().includes(term) ||
        assignment.assigned_user_name?.toLowerCase().includes(term) ||
        assignment.assigned_user_email?.toLowerCase().includes(term) ||
        assignment.assigned_parrot_name?.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter === 'assigned') {
      filtered = filtered.filter(a => a.assigned_user_id);
    } else if (statusFilter === 'unassigned') {
      filtered = filtered.filter(a => !a.assigned_user_id);
    } else if (statusFilter === 'inconsistent') {
      filtered = filtered.filter(a => inconsistentStates.some(inc => inc.id === a.id));
    }

    // Show only inconsistent if toggled
    if (showInconsistentOnly) {
      filtered = filtered.filter(a => inconsistentStates.some(inc => inc.id === a.id));
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [assignments, searchTerm, statusFilter, showInconsistentOnly, inconsistentStates, sortBy, sortOrder]);

  /**
   * Handle column sorting
   */
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  /**
   * Get sort icon for column header
   */
  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Load data on component mount
  useEffect(() => {
    fetchAssignments();
    fetchAvailableUsers();
    fetchAvailableParrots();
  }, [fetchAssignments, fetchAvailableUsers, fetchAvailableParrots]);

  if (loading) {
    return (
      <div className={`rounded-lg p-8 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
        <div className="flex items-center justify-center">
          <LoadingSpinner />
          <span className={`ml-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Loading phone number assignments...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Phone Assignment Management
          </h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Advanced tools for managing phone number assignments and fixing inconsistent states
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2 ${
              theme === 'dark'
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <span className={refreshing ? 'animate-spin' : ''}>🔄</span>
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className={`rounded-lg p-4 border ${
          theme === 'dark'
            ? 'bg-red-900/20 border-red-500/50'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center">
            <span className={`mr-2 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>⚠️</span>
            <span className={theme === 'dark' ? 'text-red-300' : 'text-red-700'}>{error}</span>
            <button
              onClick={fetchAssignments}
              className={`ml-auto underline transition-colors ${
                theme === 'dark'
                  ? 'text-red-400 hover:text-red-300'
                  : 'text-red-600 hover:text-red-700'
              }`}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
          <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {assignments.length}
          </div>
          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Numbers</div>
        </div>
        <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
          <div className="text-2xl font-bold text-teal-500">
            {assignments.filter(a => a.assigned_user_id).length}
          </div>
          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Assigned</div>
        </div>
        <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
          <div className="text-2xl font-bold text-yellow-500">
            {assignments.filter(a => !a.assigned_user_id).length}
          </div>
          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Unassigned</div>
        </div>
        <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
          <div className="text-2xl font-bold text-red-500">
            {inconsistentStates.length}
          </div>
          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Inconsistent</div>
        </div>
        <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
          <div className="text-2xl font-bold text-blue-500">
            {selectedAssignments.size}
          </div>
          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Selected</div>
        </div>
      </div>

      {/* Inconsistent states alert */}
      {inconsistentStates.length > 0 && (
        <div className={`rounded-lg p-4 border ${
          theme === 'dark'
            ? 'bg-orange-900/20 border-orange-500/50'
            : 'bg-orange-50 border-orange-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className={`mr-2 ${theme === 'dark' ? 'text-orange-400' : 'text-orange-600'}`}>⚠️</span>
              <span className={theme === 'dark' ? 'text-orange-300' : 'text-orange-700'}>
                {inconsistentStates.length} phone number{inconsistentStates.length !== 1 ? 's have' : ' has'} inconsistent assignment state{inconsistentStates.length !== 1 ? 's' : ''}
              </span>
            </div>
            <button
              onClick={() => setShowInconsistentOnly(!showInconsistentOnly)}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                showInconsistentOnly
                  ? 'bg-orange-600 text-white'
                  : theme === 'dark'
                    ? 'bg-orange-900/30 text-orange-400 hover:bg-orange-900/50'
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              {showInconsistentOnly ? 'Show All' : 'Show Only Inconsistent'}
            </button>
          </div>
        </div>
      )}

      {/* Filters and search */}
      <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <input
              type="text"
              placeholder="Search phone numbers, users, or parrots..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`px-3 py-2 rounded-lg border transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-teal-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-teal-500'
              } focus:outline-none focus:ring-1 focus:ring-teal-500`}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-3 py-2 rounded-lg border transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-teal-500'
                  : 'bg-white border-gray-300 text-gray-900 focus:border-teal-500'
              } focus:outline-none focus:ring-1 focus:ring-teal-500`}
            >
              <option value="all">All Status</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
              <option value="inconsistent">Inconsistent</option>
            </select>
          </div>
          
          {selectedAssignments.size > 0 && (
            <div className="flex items-center space-x-2">
              <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {selectedAssignments.size} selected
              </span>
              <button
                onClick={() => setShowBulkActions(true)}
                className="px-3 py-1 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 transition-colors dark:text-white"
              >
                Bulk Actions
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Assignments table */}
      <div className={`rounded-lg overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  <input
                    type="checkbox"
                    checked={selectedAssignments.size > 0 && selectedAssignments.size === filteredAndSortedAssignments.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 dark:border-gray-600"
                  />
                </th>
                <th 
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-600'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  onClick={() => handleSort('phone_number')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Phone Number</span>
                    <span>{getSortIcon('phone_number')}</span>
                  </div>
                </th>
                <th 
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-600'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    <span>{getSortIcon('status')}</span>
                  </div>
                </th>
                <th 
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-600'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  onClick={() => handleSort('assigned_user_name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Assigned User</span>
                    <span>{getSortIcon('assigned_user_name')}</span>
                  </div>
                </th>
                <th 
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-600'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  onClick={() => handleSort('assigned_parrot_name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Assigned Parrot</span>
                    <span>{getSortIcon('assigned_parrot_name')}</span>
                  </div>
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Issues
                </th>
                <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredAndSortedAssignments.map((assignment) => {
                const isSelected = selectedAssignments.has(assignment.id);
                const isInconsistent = inconsistentStates.some(inc => inc.id === assignment.id);
                
                return (
                  <tr
                    key={assignment.id}
                    className={`transition-colors ${
                      isInconsistent
                        ? theme === 'dark'
                          ? 'bg-red-900/10 border-l-4 border-red-500'
                          : 'bg-red-50 border-l-4 border-red-500'
                        : theme === 'dark'
                          ? 'hover:bg-gray-700'
                          : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleAssignmentSelection(assignment.id, e.target.checked)}
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {assignment.phone_number}
                      </div>
                      <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {assignment.friendlyName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        assignment.assigned_user_id
                          ? theme === 'dark' ? 'bg-teal-900/20 text-teal-400' : 'bg-teal-100 text-teal-800'
                          : theme === 'dark' ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {assignment.assigned_user_id ? 'assigned' : 'unassigned'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {assignment.assigned_user_name || (
                          <span className={`italic ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Unassigned</span>
                        )}
                      </div>
                      {assignment.assigned_user_email && (
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          {assignment.assigned_user_email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {assignment.assigned_parrot_name || (
                          <span className={`italic ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>None</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isInconsistent && (
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          theme === 'dark' ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-800'
                        }`}>
                          Inconsistent
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {isInconsistent && (
                          <button
                            onClick={() => handleFixInconsistentState(assignment)}
                            className="text-orange-400 hover:text-orange-300 transition-colors"
                            title="Fix inconsistent state"
                          >
                            🔧
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedAssignment(assignment);
                            setShowAssignmentModal(true);
                          }}
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                          title="Edit assignment"
                        >
                          ✏️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredAndSortedAssignments.length === 0 && (
          <div className="p-8 text-center">
            <div className={`mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No assignments match the current filters
            </div>
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setShowInconsistentOnly(false);
              }}
              className={`underline transition-colors ${
                theme === 'dark' ? 'text-teal-400 hover:text-teal-300' : 'text-teal-600 hover:text-teal-700'
              }`}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Bulk Actions Modal */}
      {showBulkActions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg max-w-md w-full border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Bulk Actions
                </h3>
                <button
                  onClick={() => setShowBulkActions(false)}
                  className={`text-gray-400 hover:text-gray-600 ${theme === 'dark' ? 'hover:text-gray-300' : ''}`}
                >
                  ✕
                </button>
              </div>

              <div className={`mb-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {selectedAssignments.size} assignment{selectedAssignments.size !== 1 ? 's' : ''} selected
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleBulkOperation('unassign')}
                  className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors dark:text-white"
                >
                  Unassign All Selected
                </button>
                <button
                  onClick={() => {
                    setShowBulkActions(false);
                    setShowBulkAssignModal(true);
                  }}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors dark:text-white"
                >
                  Bulk Assign to User
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to release ${selectedAssignments.size} phone number${selectedAssignments.size !== 1 ? 's' : ''}? This action cannot be undone.`)) {
                      handleBulkOperation('release');
                    }
                  }}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors dark:text-white"
                >
                  Release All Selected
                </button>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowBulkActions(false)}
                  className={`w-full px-4 py-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneAssignmentManager;
